'use server'

import { createClient } from '@/lib/supabase/server'
import { computeSrsUpdate } from '@/lib/srs'
import type { ActivityResult } from '@/lib/activity'
import { MIN_GRADED_TOTAL, gradeTopic } from '@/lib/grammar/progress'
import { STRONG_SESSIONS, isStrongSession } from '@/lib/grammar/lesson-plan'
import { TOPIC_BY_ID } from '@/lib/grammar/topics'

export type TrainerMistake = { wrong: string; correct: string; explanation: string }
// Откуда пришёл результат: тренажёр или шаг «Закрепи правило» в уроке
// (grammar_mistakes.source; в журнале ошибок урок и тренажёр различимы).
export type TopicResultSource = 'trainer' | 'lesson'

// Результат темы в сессии тренажёра: SRS-интервал темы, журнал ошибок,
// XP и серия (record_grammar → apply_activity, потолок 30 XP/день).
// strong_sessions — сессии от 80 % с первой попытки (lesson-plan.ts): пока их
// меньше трёх, правило не закреплено и возвращается в повторение на следующий день.
// Один вызов на тему, а не пакетом в конце — брошенная на середине сессия
// всё равно засчитает пройденные темы.
export async function submitTopicResult(
  topicId: string,
  correct: number,
  total: number,
  mistakes: TrainerMistake[],
  timezone: string,
  seconds: number,
  source: TopicResultSource = 'trainer',
  // В шаге урока с несколькими темами XP и серию начисляет только первый вызов:
  // шаг остаётся одним занятием, как раньше.
  recordActivity = true,
): Promise<(ActivityResult & { strong_sessions: number }) | { strong_sessions: number; xp_gained: 0 }> {
  if (!TOPIC_BY_ID[topicId]) throw new Error('Неизвестная тема')
  if (!Number.isInteger(correct) || !Number.isInteger(total) || total < 1 || total > 30 || correct < 0 || correct > total) {
    throw new Error('Некорректный результат')
  }

  const supabase = await createClient()
  const { data: claimsData, error: claimsError } = await supabase.auth.getClaims()
  if (claimsError || !claimsData?.claims) throw new Error('Unauthorized')
  const userId = claimsData.claims.sub

  const { data: current } = await supabase
    .from('user_grammar_progress')
    .select('srs_interval, ease_factor, next_review_at, attempts, correct, sessions, strong_sessions')
    .eq('user_id', userId)
    .eq('topic_id', topicId)
    .maybeSingle()

  // Короткая сессия (разминка из двух заданий в шаге урока, lesson-plan.ts
  // WARMUP_PLAN; обрыв) в SRS и strong_sessions не идёт: по 1-2 ответам
  // тему не оценить. Попытки, журнал ошибок и XP считаются как обычно.
  const graded = total >= MIN_GRADED_TOTAL
  const update = graded
    ? computeSrsUpdate({ srsInterval: current?.srs_interval ?? 0, easeFactor: current?.ease_factor ?? 2.5 }, gradeTopic(correct, total))
    : {
        srsInterval: current?.srs_interval ?? 0,
        easeFactor: current?.ease_factor ?? 2.5,
        nextReviewAt: new Date(current?.next_review_at ?? Date.now()),
      }
  const now = new Date().toISOString()
  const strong = (current?.strong_sessions ?? 0) + (graded && isStrongSession(correct, total) ? 1 : 0)
  // Не закреплено — завтра снова (20 часов: «завтра» при любом часовом поясе
  // и не раньше, чем через ночь).
  const tomorrow = new Date(Date.now() + 20 * 3600 * 1000)
  const nextReview = graded && strong < STRONG_SESSIONS && update.nextReviewAt > tomorrow ? tomorrow : update.nextReviewAt

  const { error: upsertError } = await supabase.from('user_grammar_progress').upsert(
    {
      user_id: userId,
      topic_id: topicId,
      srs_interval: update.srsInterval,
      ease_factor: update.easeFactor,
      next_review_at: nextReview.toISOString(),
      attempts: (current?.attempts ?? 0) + total,
      correct: (current?.correct ?? 0) + correct,
      sessions: (current?.sessions ?? 0) + 1,
      strong_sessions: strong,
      last_reviewed_at: now,
    },
    { onConflict: 'user_id,topic_id' },
  )
  if (upsertError) throw new Error(upsertError.message)

  // Журнал сессий для метрик закрепления на /founder (тикет 19). Ошибку только
  // пишем в лог: без таблицы (миграция ещё не применена) или при сбое базы
  // ученик всё равно получает результат, XP и серию.
  const { error: sessionError } = await supabase.from('grammar_sessions').insert({
    user_id: userId,
    topic_id: topicId,
    source,
    attempts: total,
    correct,
    session_no: (current?.sessions ?? 0) + 1,
    strong_after: strong,
  })
  if (sessionError) console.error('[grammar] сессия не записана в grammar_sessions:', sessionError.message)

  if (mistakes.length > 0) {
    const rows = mistakes.slice(0, 20).map((m) => ({
      user_id: userId,
      topic_id: topicId,
      source,
      wrong: m.wrong.slice(0, 200),
      correct: m.correct.slice(0, 200),
      explanation: m.explanation.slice(0, 500),
    }))
    const { error } = await supabase.from('grammar_mistakes').insert(rows)
    if (error) throw new Error(error.message)
  }

  if (!recordActivity) return { strong_sessions: strong, xp_gained: 0 }
  const { data: activity, error: activityErr } = await supabase.rpc('record_grammar', {
    p_timezone: timezone,
    p_seconds: seconds,
  })
  if (activityErr) throw new Error(activityErr.message)
  return { ...(activity as ActivityResult), strong_sessions: strong }
}
